import {
  AfterViewInit,
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  signal,
} from '@angular/core';
import { Components } from '../../components';
import { OfferingsService } from '../../services/offerings.service';
import { MulesoftOffering } from '../../models/mulesoft-offering';
import { DynamicModule } from 'ng-dynamic-component';
import { ActivatedRoute } from '@angular/router';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { filter, map, switchMap } from 'rxjs';
import { BreadcrumbsComponent } from '../../components/shared/breadcrumbs/breadcrumbs.component';
import { RdsSidenavModule } from '@rds/angular-components';
import { OfferingSkeletonComponent } from '../../components/panorama/offering-skeleton/offering-skeleton.component';
import { AssistantWidgetComponent } from '../../components/panorama/assistant-widget/assistant-widget.component';
import { FeatureFlagDirective } from '../../directives/feature-flag.directive';

@Component({
  selector: 'inexp-offering-page',
  imports: [
    DynamicModule,
    BreadcrumbsComponent,
    RdsSidenavModule,
    OfferingSkeletonComponent,
    AssistantWidgetComponent,
    FeatureFlagDirective,
  ],
  templateUrl: './offering-page.component.html',
  styleUrl: './offering-page.component.scss',
})
export class OfferingPageComponent implements AfterViewInit, OnDestroy {
  story = { content: null, name: '' };
  components = Components;
  componentsArray: any[] = [];
  sections: string[] = [];
  knowledgeSections: string[] = [];
  extraSections: string[] = [];
  activeSection = signal<string>('');
  breadcrumbs = signal<any[]>([]);
  isLoading = signal(true);

  private readonly _route = inject(ActivatedRoute);
  private readonly _offeringsService = inject(OfferingsService);
  private _observer?: IntersectionObserver;

  /** Offering slug from the route. */
  private readonly _slug = toSignal(
    this._route.paramMap.pipe(map((params) => params.get('name') ?? '')),
    { initialValue: '' },
  );

  /** The offering, fetched directly by slug from the Process API. */
  private readonly _offering = toSignal(
    toObservable(this._slug).pipe(
      filter((slug): slug is string => !!slug),
      switchMap((slug) => this._offeringsService.fetchPublishedOffering(slug)),
    ),
    { initialValue: undefined },
  );

  /** Display name of the offering in view; empty until it resolves. */
  readonly offeringName = computed(() => this._offering()?.displayName ?? '');

  /** Phrase describing what a follow-up is about, for the chat's opening line. */
  readonly assistantContext = computed(() => {
    const name = this.offeringName();
    return name ? `the "${name}" offering` : '';
  });

  /**
   * What the assistant panel is asked on arrival. Empty while the offering is
   * still in flight, which is what keeps the panel quiet until there is
   * something to summarise.
   */
  readonly assistantSummaryPrompt = computed(() => {
    const offering = this._offering();
    if (!offering?.displayName) {
      return '';
    }
    const type = offering.offeringType ? ` (${offering.offeringType})` : '';
    return (
      `Summarise the Panorama offering "${offering.displayName}"${type} in two ` +
      'or three sentences: what it is, who it is for, and when someone would ' +
      'choose it. Do not invent capabilities it does not have.'
    );
  });

  /**
   * The same request, as a reader would put it, for the chat's transcript.
   *
   * The prompt above cannot serve: it is written about the reader in the third
   * person and tells the model how long to be. This is the one composed line in
   * the handover — on the search page the reader's own words exist, because they
   * typed them in the hero, but here they opened a page and asked nothing.
   */
  readonly assistantOpeningQuestion = computed(() => {
    const name = this._offering()?.displayName;
    return name ? `Tell me about ${name}` : '';
  });

  constructor() {
    // Re-render whenever the fetched offering resolves or the published set
    // (used to resolve related-offering refs) loads.
    effect(() => {
      const offering = this._offering();
      if (!offering) {
        // Fetch in flight (or unknown slug); keep the skeleton.
        return;
      }
      const components = this._offeringsService.buildOfferingComponents(offering);
      this.renderOffering(components, offering);
    });
  }

  private renderOffering(
    componentsArray: any[],
    offering: MulesoftOffering | undefined,
  ): void {
    this.componentsArray = componentsArray;
    this.isLoading.set(false);

    this.sections = this.componentsArray
      .map((c) => c.section)
      .filter((section) => section !== undefined);

    this.knowledgeSections = this.componentsArray
      .filter((c) =>
        c.knowledgeSection &&
        Object.values(c).filter(Array.isArray).some((arr) => arr.some((item) => item?.published === true))
      )
      .map((c) => c.knowledgeSection);

    this.extraSections = this.componentsArray
      .map((c) => c.extraSection)
      .filter((extraSection) => extraSection !== undefined);

    if (offering) {
      this.story = {
        content: { displayName: offering.displayName, slug: offering.slug } as any,
        name: offering.offeringName ?? offering.displayName ?? '',
      };
      this.breadcrumbs.set(this.buildBreadcrumbs(offering));
    }

    this._observer?.disconnect();
    setTimeout(() => this.setupIntersectionObserver(), 500);
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.setupIntersectionObserver();
    }, 500);
  }

  ngOnDestroy() {
    this._observer?.disconnect();
  }

  buildBreadcrumbs(offering: MulesoftOffering) {
    return [
      {
        text: 'Home',
        link: '/',
      },
      {
        text: 'Offerings',
        link: '/search',
        queryParams: { q: '' },
      },
      {
        text: offering.displayName,
        link: '/offerings/' + offering.slug,
      },
    ];
  }

  scrollTo(id: string) {
    const el = document.getElementById(id);
    if (!el) return;

    this.activeSection.set(id);

    const targetPosition =
      el.getBoundingClientRect().top + window.pageYOffset - 100;
    const startPosition = window.pageYOffset;
    const distance = targetPosition - startPosition;
    const duration = 600;
    let startTime: number | null = null;

    const animation = (currentTime: number) => {
      startTime ??= currentTime;
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);

      const ease = 1 - (1 - progress) * (1 - progress);

      window.scrollTo(0, startPosition + distance * ease);

      if (timeElapsed < duration) {
        requestAnimationFrame(animation);
      }
    };

    requestAnimationFrame(animation);
  }

  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '-10% 0px -80% 0px',
      threshold: 0,
    };

    this._observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          const id = entry.target.getAttribute('id');
          if (id) {
            this.activeSection.set(id);
          }
        }
      }
    }, options);

    for (const section of this.sections) {
      const element = document.getElementById(section);
      if (element) {
        this._observer?.observe(element);
      }
    }

    for (const knowledgeSection of this.knowledgeSections) {
      const element = document.getElementById(knowledgeSection);
      if (element) {
        this._observer?.observe(element);
      }
    }
  }
}
